/**
 * <unbridaled-diamond> - the Unbridaled Diamonds app, boxed up so the product section barely has
 * to know it is there. Rendered by snippets/unbridaled-diamond.liquid.
 *
 * How the app attaches itself, which is the whole reason this element exists:
 *
 *   - it mounts its picker into #unbridaled-container, then finds the cart form by walking up from
 *     there, so the container has to sit inside a form posting to /cart/add
 *   - it looks for the theme's add to cart button inside that same form, by its own list of
 *     selectors starting with .add-to-cart, and tags the first one it finds
 *   - it appends its own hidden properties[] inputs to the form as the shopper picks - _grouping
 *     among them, which is what pairs the ring with its diamond in the cart
 *   - it follows the selected variant through the url, so keeping ?variant= current is the only
 *     handoff the section has to make, and it already does that
 *   - it writes the ring and the chosen diamond as one combined price, into whatever
 *     window.ubDS.priceSelector names - which it reads ahead of the selector set in its own admin
 *
 * Add to cart then runs in two passes. The app intercepts the first click on the button it tagged,
 * during the capture phase, and stops it dead. It adds the diamond itself, then clicks the button
 * a second time with its own listener detached - and that pass reaches the section, which adds the
 * ring. So the section's click handler needs no special case: it simply runs on the second pass.
 */
class UnbridaledDiamond extends HTMLElement {

    connectedCallback() {
        this.form = this.querySelector(".unbridaledDiamondForm");

        const button = document.querySelector(this.dataset.addToCart);

        if (!button) {
            console.warn(`Unbridaled: no add to cart button matching ${this.dataset.addToCart}`);
            return;
        }

        // the app searches the form and nothing else, and .add-to-cart is the first selector it
        // tries. moving the button keeps its listeners, so the section is none the wiser
        button.classList.add("add-to-cart");
        this.form.append(button);

        this.claimPrice();
    }

    /*
     * The app ships with a price selector in its admin that still names the old product page's
     * element, and window.ubDS.priceSelector overrides it. Its own script sets that during parse
     * and its bundle is deferred after this one, so overwriting it here lands in between.
     */
    claimPrice() {
        if (!window.ubDS || !this.dataset.price) {
            return;
        }

        window.ubDS.priceSelector = this.dataset.price;

        // it formats every price it writes with the currency variant of the money format, which
        // spells out the currency. the section's prices do not, and the two sit side by side
        window.ubDS.money_with_currency_format = window.ubDS.money_format || window.ubDS.money_with_currency_format;

        // the app mounts long after this runs, and the price is its to write from that moment.
        // tell the section when that happens rather than leaving it to find out on the next
        // option change, by which point it would have shown a price the app had already replaced
        const observer = new MutationObserver(() => {
            if (!this.ownsPrice) {
                return;
            }

            observer.disconnect();
            this.dispatchEvent(new CustomEvent("unbridaled:mounted"));
        });

        observer.observe(this, {childList: true, subtree: true});
    }

    /**
     * Whether the app has rendered and taken the price over. It prices the ring and the diamond as
     * one total, which the section cannot work out on its own, so the section leaves the price
     * alone from here - but only once the app is actually there to write it.
     * @returns {Boolean} True once the app has mounted its picker
     */
    get ownsPrice() {
        return !!this.dataset.price && !!this.querySelector(".ub-ds-root");
    }

    /**
     * The line item properties the app has stamped onto its form for the ring - _grouping, ring
     * size, carat. Read at add to cart, since the app writes them as the shopper picks.
     * @returns {Object} Property name to value
     */
    get properties() {
        const properties = {};

        new FormData(this.form).forEach(function (value, name) {
            const property = name.match(/^properties\[(.*)]$/);

            if (!property) {
                return;
            }

            properties[property[1]] = value;
        });

        return properties;
    }
}

customElements.define("unbridaled-diamond", UnbridaledDiamond);
